import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { Reveal, SectionHeader } from "./Reveal";
import { useLang } from "../context/LangContext";

export default function Timeline() {
  const { t } = useLang();
  const trackRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start 75%", "end 55%"],
  });

  return (
    <section id="timeline" data-testid="timeline-section" className="relative py-20 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          index={t.timeline.index}
          eyebrow={t.timeline.eyebrow}
          title={t.timeline.title}
          description={t.timeline.desc}
        />

        <div ref={trackRef} className="relative">
          <div className="absolute top-0 bottom-0 right-[26px] lg:right-1/2 w-px bg-[#E8DFC5] lg:translate-x-1/2" />
          <motion.div
            style={{ scaleY: scrollYProgress }}
            className="absolute top-0 bottom-0 right-[26px] lg:right-1/2 w-px bg-emerald-500 origin-top lg:translate-x-1/2 shadow-[0_0_16px_rgba(16,185,129,0.6)]"
          />

          <div className="flex flex-col gap-14 lg:gap-24">
            {t.timeline.milestones.map((m, i) => {
              const left = i % 2 === 1;
              return (
                <div key={m.year} className="relative lg:grid lg:grid-cols-2 lg:gap-20 items-center">
                  <motion.span
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
                    className="absolute top-2 right-[13px] lg:right-1/2 w-7 h-7 rounded-full bg-[#FBF6E9] border-2 border-emerald-500 flex items-center justify-center lg:translate-x-1/2 z-10 shadow-[0_0_20px_rgba(16,185,129,0.45)]"
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  </motion.span>

                  <Reveal
                    delay={0.1}
                    className={`pr-14 lg:pr-0 ${left ? "lg:col-start-2 lg:pr-4" : "lg:col-start-1 lg:pl-4"}`}
                  >
                    <div
                      data-testid={`timeline-milestone-${m.year}`}
                      className="group rounded-lg border border-[#E8DFC5] bg-white p-7 lg:p-9 transition-all duration-300 hover:border-emerald-500/50 hover:bg-[#F5EEDC] hover:-translate-y-1"
                    >
                      <span dir="ltr" className="block font-accent font-bold text-4xl sm:text-5xl text-emerald-500/90 mb-4 text-right">
                        {m.year}
                      </span>
                      <h3 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-3">
                        {m.title}
                      </h3>
                      <p className="text-base leading-relaxed text-stone-500">{m.text}</p>
                    </div>
                  </Reveal>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
